import { t } from "@lingui/macro";
import graphql from "babel-plugin-relay/macro";
import { useSetAtom } from "jotai";
import { useState } from "react";
import { PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay";

import { NotificationsUpdateModal } from "@/features/NotificationsUpdateModal";
import { ManageNotificationsModal } from "@/screens/Profile/components/AccountSettings/components/ManageNotificationsModal";
import { isVerifyingEmailOrPhoneAtom } from "@/screens/Profile/components/AccountSettings/components/ManageNotificationsModal/atoms/stores";
import { useTrackEvent } from "@/utils/Analytics/Mixpanel/hooks/useTrackEvent";
import { useFlags } from "@/utils/launchDarkly";
import { Button } from "@whoosh/design-system/src/components/controls";
import {
  BellIcon,
  CheckIcon,
  BlockIcon,
} from "@whoosh/design-system/src/components/icons_v2/general";
import {
  Box,
  HStack,
  Text,
  VStack,
} from "@whoosh/design-system/src/components/primitive";

import { NotificationsInnerQuery } from "./__generated__/NotificationsInnerQuery.graphql";
import { NotificationsInner_slot$key } from "./__generated__/NotificationsInner_slot.graphql";

type Props = {
  slotRef: NotificationsInner_slot$key;
  notificationPreferencesRef: PreloadedQuery<NotificationsInnerQuery>;
  refetchNotificationPreferences: () => void;
};

export const NotificationsInner = ({
  slotRef,
  notificationPreferencesRef,
  refetchNotificationPreferences,
}: Props) => {
  const { waitlistRestrictions } = useFlags();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isManageNotificationsOpen, setIsManageNotificationsOpen] =
    useState(false);
  const setLegacyVerificationMode = useSetAtom(isVerifyingEmailOrPhoneAtom);

  const trackEvent = useTrackEvent();

  const session = usePreloadedQuery(
    NotificationsInnerQueryString,
    notificationPreferencesRef
  );
  const notificationPreferences =
    session?.session?.user?.notificationPreferencesChannels;

  const emailEnabled =
    notificationPreferences?.requestedTimeAvailable?.includes("EMAIL");
  const smsEnabled =
    notificationPreferences?.requestedTimeAvailable?.includes("SMS");
  const pushEnabled =
    notificationPreferences?.requestedTimeAvailable?.includes("PUSH");

  const slot = useFragment(NotificationsInnerFragmentString, slotRef);
  const supportsPublic = slot?.agenda?.facility?.club?.supportsPublic ?? false;
  const clientCanFulfillRequests = supportsPublic
    ? slot?.agenda?.facility?.publicBookingPermissionSet
        ?.clientCanFulfillRequests ?? false
    : slot?.agenda?.facility?.privateBookingPermissionSet
        ?.clientCanFulfillRequests ?? false;

  const handleRequestVerification = (mode: "email" | "phone") => {
    setLegacyVerificationMode(mode);
    setIsManageNotificationsOpen(true);
    setIsModalOpen(false);
  };

  if (!waitlistRestrictions || !clientCanFulfillRequests) return null;

  return (
    <VStack
      gap={5}
      paddingTop={5}
      paddingBottom={5}
      paddingHorizontal={5}
      borderColor="outline-default"
      borderWidth={1}
      borderLeftWidth={0}
      borderRightWidth={0}
    >
      <Text variant="mainHeading">{t`Notifications`}</Text>
      <Box
        padding={2}
        backgroundColor="surface-main-primary-subtle"
        gap={2}
        borderRadius="2"
      >
        <Box
          backgroundColor="surface-primary"
          padding={3}
          gap={2}
          borderRadius="2"
        >
          <Text variant="mainSmall">{t`If a tee time becomes available, we’ll
notify you via...`}</Text>

          <HStack gap={1}>
            <Tag text={t`Email`} isDisabled={!emailEnabled} />
            <Tag text={t`SMS`} isDisabled={!smsEnabled} />
            <Tag text={t`Push`} isDisabled={!pushEnabled} />
          </HStack>
        </Box>
        <Box backgroundColor="surface-primary" borderRadius="2">
          <Button
            accessibilityLabel={t`Update Notification Preferences Button`}
            width="100%"
            variant="ghost"
            size="tiny"
            testID="update-notification-preferences-button"
            leftIcon={<BellIcon />}
            onPress={() => {
              setIsModalOpen(true);
              trackEvent("BookingDetail.WaitlistNotificationModalOpened", {});
            }}
          >
            {t`Update Notification Preferences`}
          </Button>
        </Box>
      </Box>
      <NotificationsUpdateModal
        isOpen={isModalOpen}
        setIsOpen={setIsModalOpen}
        showGuide={true}
        onRequestVerification={handleRequestVerification}
        onSaveSuccess={refetchNotificationPreferences}
      />
      {isManageNotificationsOpen ? (
        <ManageNotificationsModal
          isOpen={isManageNotificationsOpen}
          setIsOpen={setIsManageNotificationsOpen}
          onVerificationComplete={
            waitlistRestrictions
              ? () => {
                  setIsManageNotificationsOpen(false);
                  setIsModalOpen(true);
                }
              : undefined
          }
        />
      ) : null}
    </VStack>
  );
};

const Tag = ({ text, isDisabled }: { text: string; isDisabled: boolean }) => {
  if (isDisabled) {
    return (
      <HStack
        backgroundColor="surface-secondary"
        testID={`notification-tag-${text}-disabled`}
        padding={2}
        borderRadius="2"
        gap={1}
        alignItems="center"
      >
        <Text variant="mainTiny" color="text-secondary">
          {text}
        </Text>
        <BlockIcon color="text-secondary" width={12} height={12} />
      </HStack>
    );
  } else {
    return (
      <HStack
        backgroundColor="surface-main-primary-subtlest"
        testID={`notification-tag-${text}-enabled`}
        padding={2}
        borderRadius="2"
        gap={1}
        alignItems="center"
      >
        <Text variant="mainTiny">{text}</Text>
        <CheckIcon />
      </HStack>
    );
  }
};

export const NotificationsInnerFragmentString = graphql`
  fragment NotificationsInner_slot on Slot {
    agenda {
      facility {
        publicBookingPermissionSet {
          clientCanFulfillRequests
        }
        privateBookingPermissionSet {
          clientCanFulfillRequests
        }
        club {
          supportsPublic
        }
      }
    }
  }
`;

export const NotificationsInnerQueryString = graphql`
  query NotificationsInnerQuery {
    session {
      user {
        id
        ... on MemberUser {
          notificationPreferencesChannels {
            requestedTimeAvailable
          }
        }
      }
    }
  }
`;
